import { Injectable, OnDestroy } from '@angular/core';
import { SolShippingAndHandlingDialogComponent } from '../../Components/dialogs/sol/sol-shipping-and-handling-dialog/sol-shipping-and-handling-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { SolEstimateWarrantyDialogComponent } from '../../Components/dialogs/sol/sol-estimate-warranty-dialog/sol-estimate-warranty-dialog.component';
import { SolEstimateEscalationDialogComponent } from '../../Components/dialogs/sol/sol-estimate-escalation-dialog/sol-estimate-escalation-dialog.component';
import { SolEstimateRiskDialogComponent } from '../../Components/dialogs/sol/sol-estimate-risk-dialog/sol-estimate-risk-dialog.component';
import { Subject } from 'rxjs';
import { SolMaterialEscalationDialogDataModel } from '../../models/sol-estimate-table/SolMaterialEscalationDialogDataModel';
import { SolEstimateRiskData } from '../../models/sol-estimate-table/SolEstimateRiskData';
import { SolEstimateRiskDialogData } from '../../models/sol-estimate-table/SolEstimateRiskDialogData';
import { ShippingHandlingData } from '../../models/sol-estimate-table/ShippingHandlingData';
import { WarrantyDialogResult } from '../../models/sol-estimate-table/WarrantyData';
import { CONSTANTS } from '../../Constants/Constants';
import { CommonDataService } from '../common-data.service';
import { EstimateStatus } from '../../models/dtos';
import { CommonFunctionsService } from '../common-functions.service';
import { NumericFieldConfig } from '../../shared/numeric/numeric.types';

@Injectable({
  providedIn: 'root'
})
export class SolEstimateTableCommonFunctionsService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();

  /** Emits shipping data when the Shipping & Handling dialog commits. */
  readonly updatedShippingData = new Subject<ShippingHandlingData[]>();

  /** Emits warranty data when the Warranty dialog commits. */
  readonly updatedWarrantyData = new Subject<WarrantyDialogResult>();

  /** Emits escalation data when the Escalation dialog commits. */
  readonly updatedEscalationData = new Subject<SolMaterialEscalationDialogDataModel>();

  /** Emits risk data when the Risk dialog commits. */
  readonly updatedRiskData = new Subject<SolEstimateRiskData>();

  /** Shared across every calculated GM%/MU% input in the sol-estimate-table rows. */
  readonly calculatedPercentageConfig: NumericFieldConfig = {
    kind: 'percentage',
    allowNegative: true,
    max: CONSTANTS.SELL_AS_MAX_VALUE,
    step: CONSTANTS.ONE,
    useGrouping: false,
    showStepper: false,
    revertToLastValidValue: true
  };

  /** Shared across every sell-price input in the sol-estimate-table rows. */
  readonly sellPriceConfig: NumericFieldConfig = {
    kind: 'currency',
    allowNegative: true,
    max: CONSTANTS.SELL_AS_MAX_VALUE,
    showStepper: false,
    step: CONSTANTS.ONE,
    useGrouping: true,
    revertToLastValidValue: true
  };

  /** Shared across the tax material/labor rate inputs. */
  readonly taxRateConfig: NumericFieldConfig = {
    kind: 'percentage',
    allowNegative: false,
    max: CONSTANTS.NINE_HUNDRED_NINETY_NINE_POINT_NINE_NINE,
    step: CONSTANTS.ONE,
    useGrouping: false,
    showStepper: false,
    revertToLastValidValue: true
  };

  /** Shared across the technology fee markUp%/GM% inputs. */
  readonly technologyFeeConfig: NumericFieldConfig = {
    kind: 'percentage',
    allowNegative: false,
    max: CONSTANTS.NINETY_NINE_POINT_NINE_NINE,
    step: CONSTANTS.ONE,
    useGrouping: false,
    showStepper: false,
    revertToLastValidValue: true
  };

  constructor(
    private readonly dialog: MatDialog,
    private readonly commonDataService: CommonDataService,
    private readonly commonFunctionsService: CommonFunctionsService
  ) { }

  openShippingAndHandlingDialog(): void {
    const dialogRef = this.dialog.open(SolShippingAndHandlingDialogComponent, {
      width: CONSTANTS.SHIPPING_DIALOG_WIDTH,
      disableClose: true,
      panelClass: 'custom-dialog-container'
    });
    dialogRef.afterClosed().subscribe((data: ShippingHandlingData[] | undefined) => {
      if (data && data.length > CONSTANTS.ZERO) {
        this.updatedShippingData.next(data);
        this.commonDataService.isSaveEnabled = true;
      }
    });
  }

  openWarrantyDialog(): void {
    const dialogRef = this.dialog.open(SolEstimateWarrantyDialogComponent, {
      width: CONSTANTS.WARRANTY_DIALOG_WIDTH,
      disableClose: true,
      panelClass: 'custom-dialog-container'
    });
    dialogRef.afterClosed().subscribe((result: WarrantyDialogResult | undefined) => {
      if (result?.data && result.data.length > CONSTANTS.ZERO) {
        this.updatedWarrantyData.next(result);
        this.commonDataService.isSaveEnabled = true;
      }
    });
  }


  openEscalationDialog(escalationDialogData: SolMaterialEscalationDialogDataModel): void {
    const dialogRef = this.dialog.open(SolEstimateEscalationDialogComponent, {
      width: CONSTANTS.ESCALATION_DIALOG_WIDTH,
      data: escalationDialogData,
      disableClose: true
    });
    dialogRef.afterClosed().subscribe((result: SolMaterialEscalationDialogDataModel | undefined) => {
      if (result) {
        this.updatedEscalationData.next(result);
        this.commonDataService.isSaveEnabled = true;
      }
    });
  }

  openRiskDialog(data: SolEstimateRiskDialogData): void {
    const dialogRef = this.dialog.open(SolEstimateRiskDialogComponent, {
      width: '470px',
      data: { ...data },
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((result: { riskData: SolEstimateRiskData } | undefined) => {
      if (result?.riskData) {
        this.updatedRiskData.next(result.riskData);
      }
    });
  }

  get isEstimateSapSubmitted(): boolean {
    const estimateData = this.commonDataService.getEstimateData();
    if (estimateData && estimateData.estimateStatusId === EstimateStatus.Submitted) {
      return true;
    }
    return false;
  }

  get isSdiApplicable(): boolean {
    return this.commonFunctionsService.isSdiApplicable();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
